import {
  type GridApi,
  type GridCallbackDetails,
  type GridRowSelectionCheckboxParams,
  type GridRowSelectionModel,
  type MuiEvent,
} from '@mui/x-data-grid';
import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

type P = {
  apiRef: GridApi;
  requestParameters: Record<string, any>;
  rows: Array<{ id: string | number }>;
  totalRowCount: number;
};

/**
 * Mimics the behavior of the client-side selection model for the server-side selection model.
 * Allows selecting all rows in the datagrid, even though the data is not loaded into the client.
 *
 * Key behaviors:
 * 1. Select All (via header checkbox):
 *    - Sets shouldSelectAll to true
 *    - Returns total server-side count as number of selected rows
 *    - Selects visible rows on current page
 *
 * 2. Individual Row Selection:
 *    - Sets shouldSelectAll to false
 *    - Tracks manually selected row IDs
 *    - Returns count of manually selected rows
 *
 * The hook maintains selection state across page changes and handles cleanup when filter parameters change.
 *
 * @example
 * const mimicSelectAllBehaviorWithServerSideData = useSelectAllServerSide({
 *   apiRef,
 *   requestParameters,
 *   rows,
 *   totalRowCount
 * });
 * return <DataGrid {...mimicSelectAllBehaviorWithServerSideData} />;
 *
 * @param params - Hook configuration parameters
 * @param params.apiRef - Reference to the Data Grid API instance
 * @param params.requestParameters - Parameters used for data fetching
 * @param params.rows - Currently visible rows in the grid
 * @param params.totalRowCount - Total number of rows available on server
 *
 * @returns Configuration object for Data Grid selection
 * @returns returns.checkboxSelection - Enables checkbox selection UI
 * @returns returns.headerSelectionCheckboxProps - Props for the header checkbox
 * @returns returns.keepNonExistentRowsSelected - Preserves selection when rows are not in view
 * @returns returns.onRowSelectionModelChange - Handler for row selection changes
 * @returns returns.rowSelectionModel - Array of currently selected row IDs
 * @returns returns.selectAllRowsCount - Total number of selected rows to display
 * @returns returns.shouldSelectAll - Whether "Select All" is active
 */
export const useSelectAllServerSide = ({
  apiRef,
  requestParameters,
  rows,
  totalRowCount,
}: P) => {
  const [shouldSelectAll, setShouldSelectAll] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);

  const handleRowSelectionModelChange = useCallback(
    (event: GridRowSelectionModel) => {
      setRowSelectionModel(event);
      setShouldSelectAll(false);
    },
    [],
  );

  const handleSelectAllRows = useCallback(
    (
      { value }: GridRowSelectionCheckboxParams,
      _event: MuiEvent<ChangeEvent<HTMLElement>>,
      details: GridCallbackDetails,
    ) => {
      setShouldSelectAll(value);

      if (value) {
        // When selecting all, select current page rows
        const currentPageRowIds = rows?.map(({ id }) => id) ?? [];
        setRowSelectionModel(currentPageRowIds);
      } else {
        // Reset row selection model
        setRowSelectionModel([]);
      }
    },
    [rows],
  );

  useEffect(() => {
    if (shouldSelectAll) {
      // When select all was used and the user changes page, select the rows on the new page
      const currentPageRowIds = rows?.map(({ id }) => id) ?? [];
      setRowSelectionModel(currentPageRowIds);
    }
  }, [rows, shouldSelectAll]);

  // Subscribe to header checkbox change event for selecting / deselecting all rows
  useEffect(() => {
    if (!apiRef.current) {
      return;
    }

    const unsubscribe = apiRef.current.subscribeEvent(
      'headerSelectionCheckboxChange',
      handleSelectAllRows,
    );

    return () => {
      unsubscribe();
    };
  }, [apiRef, handleSelectAllRows]);

  const filteredParameters = useMemo(() => {
    // Ignore 'offset', because otherwise the selection would reset on every page change.
    const keysToIgnore = new Set(['offset']);

    return Object.fromEntries(
      Object.entries(requestParameters).filter(
        ([key]) => !keysToIgnore.has(key),
      ),
    );
  }, [requestParameters]);

  useEffect(() => {
    setShouldSelectAll(false);
    setRowSelectionModel([]);
  }, [JSON.stringify(filteredParameters)]);

  const selectAllRowsCount = shouldSelectAll
    ? totalRowCount
    : rowSelectionModel.length;

  const returnValue = useMemo(
    () => ({
      checkboxSelection: true,
      headerSelectionCheckboxProps: {
        checked: shouldSelectAll,
      },
      keepNonExistentRowsSelected: true,
      onRowSelectionModelChange: handleRowSelectionModelChange,
      rowSelectionModel,
      selectAllRowsCount,
      shouldSelectAll,
    }),
    [
      handleRowSelectionModelChange,
      handleSelectAllRows,
      rowSelectionModel,
      selectAllRowsCount,
      shouldSelectAll,
    ],
  );

  return returnValue;
};
